import { type FC } from 'react'

import { Button } from '@/components/button/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table/table'

import { useDialog } from '@/lib/hooks/use-dialog'
import type { ResCreateCases } from '@/lib/queries/business/v1/case/types'
import { cn } from '@/lib/utils'

import { DuplicatedRowsDialog } from './dialogs/duplicated-rows-dialog'
import { ExistingRowsDialog } from './dialogs/existing-rows-dialog'
import { InvalidRowsDialog } from './dialogs/invalid-rows-dialog'

export interface ResultProps {
  result: ResCreateCases
}

export const ImportResult: FC<ResultProps> = ({ result }) => {
  const invalidRows = result.errorRows?.invalidRows ?? []
  const duplicatedRows = result.errorRows?.duplicatedRows ?? []
  const existingRows = result.errorRows?.existedRows ?? []

  const {
    isDialogOpen: isOpenInvalidRowsDialog,
    openDialog: openInvalidRowsDialog,
    closeDialog: closeInvalidRowsDialog,
  } = useDialog()
  const {
    isDialogOpen: isOpenDuplicatedRowsDialog,
    openDialog: openDuplicatedRowsDialog,
    closeDialog: closeDuplicatedRowsDialog,
  } = useDialog()
  const {
    isDialogOpen: isOpenExistingRowsDialog,
    openDialog: openExistingRowsDialog,
    closeDialog: closeExistingRowsDialog,
  } = useDialog()

  return (
    <>
      <div className={cn('mb-4', 'font-medium')}>File import summary</div>
      <div className={cn('mb-6', 'text-sm')}>Review the results of your recent file upload</div>
      <div className={cn('mb-5', 'w-[480px]', 'rounded-md', 'border')}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <TableHead>Count</TableHead>
              {/* Intended empty cell */}
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>Total rows processed</TableCell>
              <TableCell>{result.totalCount}</TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>Successfully imported</TableCell>
              <TableCell>{result.successCount}</TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>Imported failed (invalid cases)</TableCell>
              <TableCell>{result.invalidCount}</TableCell>
              <TableCell>
                {invalidRows.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn('text-sm text-rose-600 hover:text-rose-600')}
                    onClick={openInvalidRowsDialog}
                  >
                    View Details
                  </Button>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Imported failed (duplicated cases)</TableCell>
              <TableCell>{result.duplicateCount}</TableCell>
              <TableCell>
                {duplicatedRows.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn('text-sm text-rose-600 hover:text-rose-600')}
                    onClick={openDuplicatedRowsDialog}
                  >
                    View Details
                  </Button>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Imported failed (existing cases)</TableCell>
              <TableCell>{result.existedCount}</TableCell>
              <TableCell>
                {existingRows.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn('text-sm text-rose-600 hover:text-rose-600')}
                    onClick={openExistingRowsDialog}
                  >
                    View Details
                  </Button>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <div
        className={cn(
          'flex flex-col space-y-2',
          'w-[480px]',
          'p-4',
          'text-xs font-normal text-slate-500',
          'rounded-[10px]',
          'bg-slate-50'
        )}
      >
        <p>
          <span className={cn('font-semibold')}>*Invalid Case: </span> Cases that cannot be tracked on USCIS or DOL
          sites due to incorrect formats, invalid numbers, or other errors.
        </p>
        <p>
          <span className={cn('font-semibold')}>*Duplicate Case: </span>Cases with numbers that appear more than once in
          the file.
        </p>
        <p>
          <span className={cn('font-semibold')}>*Existing Case: </span>Cases that have already been added to Lawfully
          Pro.
        </p>
      </div>

      {/* Dialogs */}
      <InvalidRowsDialog
        isOpen={isOpenInvalidRowsDialog}
        invalidCaseNumbers={invalidRows}
        close={closeInvalidRowsDialog}
      />
      <DuplicatedRowsDialog
        isOpen={isOpenDuplicatedRowsDialog}
        duplicatedCaseNumbers={duplicatedRows}
        close={closeDuplicatedRowsDialog}
      />
      <ExistingRowsDialog
        isOpen={isOpenExistingRowsDialog}
        existingCaseNumber={existingRows}
        close={closeExistingRowsDialog}
      />
    </>
  )
}
